import type { ToolCallState } from '../../stores/agent-session-store';

import { ImageGallery } from './ImageLightbox';
import { summarizeToolInput } from './tool-metadata';
import { ToolCallHeader } from './ToolCallHeader';

/**
 * Renderer for `browser_screenshot`, which is the one tool whose output is a picture.
 *
 * The generic {@link ToolCallRow} hides all tool output behind a staff-only disclosure, which is
 * right for a wall of JSON and wrong here: the image *is* the result, and a non-staff user would
 * otherwise see "Took a screenshot" and nothing at all. So this reuses that row's header — same
 * status icon, same title, same summary — and puts the picture underneath it.
 *
 * Only screenshots get a card. "Opened page  http://localhost:5173/settings" is already a
 * complete sentence, so `browser_open`/`snapshot`/`act` stay on the generic row.
 */
export function BrowserToolCard({ toolCall }: { toolCall: ToolCallState }) {
  const summary = summarizeToolInput(toolCall.name, toolCall.input);

  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-line-subtle bg-surface-1 px-3 py-2">
      <ToolCallHeader toolCall={toolCall} summary={summary} />

      {toolCall.imageUrls.length > 0 && <ImageGallery images={toolCall.imageUrls} variant="screenshot" />}

      {/* A failed shot has no image, so the reason has to be visible — and to everyone, not just
          staff, since "took a screenshot" with nothing under it reads as the UI being broken. */}
      {toolCall.isError && toolCall.output && <p className="text-2xs text-danger">{toolCall.output}</p>}
    </div>
  );
}
